import React, { useState, useEffect, useCallback, useRef } from "react";
import ProjectCard from "@/components/ProjectCard";
import BottomNavigation from "@/components/BottomNavigation";
import { Project } from "@/types";
import { Button } from "@/components/ui/button";
import { AlertCircle, Crown, LockIcon, Plus, CalendarClock, ChevronRight, CheckSquare, Loader2, Search, SlidersHorizontal, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import useSubscription from "@/contexts/SubscriptionContext";
import { Card } from "@/components/ui/card";
import { projectsAPI } from "@/api/projects";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import CheckStatusDialog from "@/components/CheckStatusDialog";
import { entriesAPI } from "@/api/entries";
import { useAuth } from "@/contexts/AuthContext";
import { subscriptionsAPI } from "@/api/subscriptions";
import { Capacitor } from "@capacitor/core";
import MobileStoreRedirect from "@/components/ui/MobileStoreRedirect";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { NotificationsBell, NotificationItem } from "@/components/NotificationsBell";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { SUPPORTED_CURRENCIES } from "@/api/currency";
import { unitsAPI } from "@/api/units";
import { supabase } from "@/integrations/supabase/client";
import { AppNotification, notificationsAPI } from "@/api/notifications";
import { Switch } from "@/components/ui/switch";

// Helper function to map database project format to the Project type
const mapDatabaseProjectToProjectType = async (dbProject: any): Promise<Project> => {
  
  
  // Get the detailed costs for the project
  let detailedCosts: any = null;
  try {
    const costsFromEntries = await projectsAPI.getProjectCostsFromEntries(dbProject.id);
    if (costsFromEntries?.detailedCosts) {
      const raw = costsFromEntries.detailedCosts as Record<string, { official?: number; unofficial?: number; total?: number }>;
      detailedCosts = { official: {}, unofficial: {}, total: {} } as any;
      Object.entries(raw).forEach(([currency, values]) => {
        detailedCosts.official[currency] = values?.official || 0;
        detailedCosts.unofficial[currency] = values?.unofficial || 0;
        detailedCosts.total[currency] = values?.total || 0;
      });
    } else {
      detailedCosts = await projectsAPI.getProjectCosts(dbProject.id);
    }
  } catch (error) {
    
    
    if (error instanceof Error) {
      
    }
    // Set detailedCosts to null to ensure we don't try to use it
    detailedCosts = null;
  }
  
  // Get upcoming check payments for the project
  let upcomingPayments = [];
  try {
    
    upcomingPayments = await projectsAPI.getProjectUpcomingCheckPayments(dbProject.id);
    
  } catch (error) {
    
    
    if (error instanceof Error) {
      
    }
  }

  // Get units for accurate area totals (fallback when details.units is missing)
  let units: Array<{ type: "flat" | "shop"; number: number; area: number }> = [];
  try {
    const { data: unitsData } = await unitsAPI.getProjectUnits(dbProject.id);
    if (Array.isArray(unitsData)) {
      units = unitsData;
    }
  } catch (error) {
    units = [];
  }

  // Prepare basic project data with try-catch for safety
  try {
    
    
    // Helper function to safely access nested properties
    const safeGet = (obj: any, path: string, defaultValue: any = 0) => {
      const parts = path.split('.');
      let current = obj;
      
      for (const part of parts) {
        if (current == null || typeof current !== 'object') {
          return defaultValue;
        }
        current = current[part];
      }
      
      return current ?? defaultValue;
    };
    
    const fallbackTryCost =
      dbProject.cost_try ??
      dbProject.details?.cost_try ??
      dbProject.details?.budget ??
      dbProject.budget ??
      0;
    const fallbackUsdCost = dbProject.cost_usd ?? dbProject.details?.cost_usd ?? 0;
    const fallbackEurCost = dbProject.cost_eur ?? dbProject.details?.cost_eur ?? 0;

    const totalTry = safeGet(detailedCosts, 'total.TRY', fallbackTryCost);
    const totalUsd = safeGet(detailedCosts, 'total.USD', fallbackUsdCost);
    const totalEur = safeGet(detailedCosts, 'total.EUR', fallbackEurCost);

    const detailsUnits = Array.isArray(dbProject.details?.units) ? dbProject.details.units : [];
    const mergedUnits = detailsUnits.length > 0 ? detailsUnits : units;
    const areaFromUnits = mergedUnits.reduce((sum: number, unit: any) => sum + (Number(unit?.area) || 0), 0);
    const fallbackArea = dbProject.details?.area ?? dbProject.area ?? 0;
    const resolvedArea = areaFromUnits > 0 ? areaFromUnits : fallbackArea;

    const project: Project = {
      id: dbProject.id,
      name: dbProject.name,
      status: dbProject.status || "active",
      progress: dbProject.progress || 0,
      costs: {
        try: totalTry || fallbackTryCost,
        usd: totalUsd || fallbackUsdCost,
        eur: totalEur || fallbackEurCost,
      },
      details: {
        parcel: dbProject.details?.parcel || dbProject.parcel || "",
        lot: dbProject.details?.lot || dbProject.lot || "",
        flats: dbProject.details?.flats ?? dbProject.flats ?? 0,
        shops: dbProject.details?.shops ?? dbProject.shops ?? 0,
        area: resolvedArea,
        costPerSquareMeter: dbProject.details?.costPerSquareMeter ?? dbProject.cost_per_square_meter ?? 0,
        units: mergedUnits,
      },
      upcomingPayments: upcomingPayments
    };

    // If we have detailed costs with the expected structure, use them
    if (detailedCosts && detailedCosts.official && detailedCosts.unofficial && detailedCosts.total) {
      
      project.detailedCosts = {
        official: {
          TRY: safeGet(detailedCosts, 'official.TRY', 0),
          USD: safeGet(detailedCosts, 'official.USD', 0),
          EUR: safeGet(detailedCosts, 'official.EUR', 0),
        },
        unofficial: {
          TRY: safeGet(detailedCosts, 'unofficial.TRY', 0),
          USD: safeGet(detailedCosts, 'unofficial.USD', 0),
          EUR: safeGet(detailedCosts, 'unofficial.EUR', 0),
        },
        total: {
          TRY: safeGet(detailedCosts, 'total.TRY', 0),
          USD: safeGet(detailedCosts, 'total.USD', 0),
          EUR: safeGet(detailedCosts, 'total.EUR', 0),
        },
      } as any;

      // Preserve additional currencies for card-level selected currency rendering.
      Object.keys(detailedCosts.total || {}).forEach((currency) => {
        (project.detailedCosts as any).official[currency] = safeGet(detailedCosts, `official.${currency}`, 0);
        (project.detailedCosts as any).unofficial[currency] = safeGet(detailedCosts, `unofficial.${currency}`, 0);
        (project.detailedCosts as any).total[currency] = safeGet(detailedCosts, `total.${currency}`, 0);
      });
    }
    
    
    return project;
  } catch (error) {
    
    
    if (error instanceof Error) {
      
    }
    
    // Return a minimal valid project object on error
    const fallbackProject: Project = {
      id: dbProject.id,
      name: dbProject.name || "Error loading project",
      status: "active" as const,
      progress: 0,
      costs: { try: 0, usd: 0, eur: 0 },
      details: { parcel: "", lot: "", flats: 0, shops: 0, area: 0, costPerSquareMeter: 0 },
      upcomingPayments: []
    };
    
    return fallbackProject;
  }
};

// Add error boundary for HomePage specifically
const HomePageErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hasError, setHasError] = useState(false);
  const [errorInfo, setErrorInfo] = useState<string | null>(null);

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      
      if (event.error?.message?.includes('is not a function')) {
        setHasError(true);
        setErrorInfo(`Function error: ${event.error.message}`);
      }
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  if (hasError) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <div className="space-y-4 text-center max-w-md p-4">
          <h1 className="text-xl font-bold text-foreground">Yükleme Hatası</h1>
          <p className="text-muted-foreground">
            Ana sayfa yüklenirken bir hata oluştu. Sayfa yenileniyor...
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md"
          >
            Sayfayı Yenile
          </button>
          {errorInfo && (
            <details className="text-sm text-left">
              <summary>Hata Detayları</summary>
              <pre className="mt-2 text-xs">{errorInfo}</pre>
            </details>
          )}
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

const HomePage: React.FC = () => {
  
  
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { status, refreshSubscription } = useSubscription();
  const { 
    isLoading: isSubscriptionLoading, 
    planType, 
    projectLimit, 
    plan, 
    active_project_count, 
    active_project_limit, 
    projects_locked, 
    has_access 
  } = status;
  const { user } = useAuth();
  const { preferences } = useUserPreferences();
  const isMobilePlatform = Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android';
  
  
  
  
  const [projects, setProjects] = useState<Project[]>([]);
  const [upcomingChecks, setUpcomingChecks] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [checkStatusDialogOpen, setCheckStatusDialogOpen] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState<any | null>(null);
  const [activeProjects, setActiveProjects] = useState<Project[]>([]); 
  const [completedProjects, setCompletedProjects] = useState<Project[]>([]);
  const [isCheckingLimits, setIsCheckingLimits] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showOfficialOnly, setShowOfficialOnly] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState("TRY");
  const [projectUnitStats, setProjectUnitStats] = useState<
    Array<{ projectId: string; projectName: string; total: number; sold: number; remaining: number }>
  >([]);

  const mapNotificationToItem = useCallback(
    (notification: AppNotification): NotificationItem => {
      const rawAmount = Number(notification.data?.amount ?? NaN);
      const amountText = Number.isFinite(rawAmount)
        ? `${new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 }).format(rawAmount)} ₺`
        : undefined;
      const dueInDays = Number(notification.data?.dueInDays);
      const tone = notification.type === "check_payment" && Number.isFinite(dueInDays) && dueInDays <= 0
        ? "error"
        : notification.type === "check_payment"
          ? "warning"
          : "brand";
      const deepLink = typeof notification.data?.deepLink === "string"
        ? String(notification.data.deepLink)
        : undefined;

      return {
        id: notification.id,
        title: notification.title,
        description: notification.body,
        meta: amountText,
        tone,
        isRead: notification.isRead,
        onClick: async () => {
          try {
            await notificationsAPI.markNotificationRead(notification.id);
            setNotifications((prev) =>
              prev.map((item) => (item.id === notification.id ? { ...item, isRead: true } : item)),
            );
            if (deepLink) {
              navigate(deepLink);
            }
          } catch {
            toast({
              title: t("common.error"),
              description: t("notifications.loading_error"),
              variant: "destructive",
            });
          }
        },
      };
    },
    [navigate, t, toast]
  );

  // Add refs for tracking fetch state and preventing loops
  const isCurrentlyFetching = useRef(false);
  const fetchDataCallCount = useRef(0);
  const lastFetchTime = useRef(0);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  // Add ref for event listener registration
  const unsubRef = useRef<(() => void) | null>(null);

  

  // Redirect to subscription page if user doesn't have access
  useEffect(() => {
    
    
    // Only redirect if subscription loading is complete AND has_access is explicitly false
    // Don't redirect if has_access is undefined/null (subscription status not yet determined)
    if (!isSubscriptionLoading && has_access === false && window.location.pathname !== '/pazaryeri' && !window.location.pathname.includes('/subscription')) {
      
      navigate('/subscription-expired');
    } else {
      
    }
  }, [isSubscriptionLoading, has_access, navigate]);

  // Simplified fetchData function to prevent race conditions
  const fetchData = useCallback(async () => {
    // Safety checks to prevent infinite loops
    const now = Date.now();
    
    if (isCurrentlyFetching.current) {
      
      return;
    }
    
    if (now - lastFetchTime.current < 1000) {
      
      return;
    }

    isCurrentlyFetching.current = true;
    fetchDataCallCount.current += 1;
    lastFetchTime.current = now;
    
    
    
    try {
      
      setIsLoading(true);
      setHasError(false);
      setErrorMessage(null);
      
      
      
      // Fetch projects
      try {
        
        const projectsData = await projectsAPI.getProjects();
        
        
        // Check if projectsData is an array before mapping
        if (Array.isArray(projectsData)) {
          
          const projectPromises = projectsData.map(mapDatabaseProjectToProjectType);
          const mappedProjects = await Promise.all(projectPromises);
          
          
          // Separate active and completed projects
          const active = mappedProjects.filter(p => p.status !== "completed");
          const completed = mappedProjects.filter(p => p.status === "completed");
          
          
          
          
          setActiveProjects(active);
          setCompletedProjects(completed);
          setProjects(mappedProjects); // Keep this for backward compatibility

          // Build project-level unit summary (Toplam / Satılan / Kalan)
          try {
            const activeProjectIds = active.map((project) => project.id);
            let salesEntries: Array<{ project_id: string; description: string; metric_details: any }> = [];

            if (activeProjectIds.length > 0) {
              const { data: salesData, error: salesError } = await supabase
                .from("entries")
                .select("project_id, description, metric_details")
                .eq("type", "sale")
                .in("project_id", activeProjectIds);

              if (!salesError && Array.isArray(salesData)) {
                salesEntries = salesData as Array<{ project_id: string; description: string; metric_details: any }>;
              }
            }

            const soldKeys = new Set<string>();
            const soldByProject = new Map<string, number>();

            salesEntries.forEach((entry) => {
              let unitType: string | null = null;
              let unitNumber: number | null = null;

              if (entry.metric_details) {
                try {
                  const metricDetails =
                    typeof entry.metric_details === "string"
                      ? JSON.parse(entry.metric_details)
                      : entry.metric_details;

                  if (metricDetails?.unitType && metricDetails?.unitNumber) {
                    unitType = String(metricDetails.unitType);
                    unitNumber = Number(metricDetails.unitNumber);
                  }
                } catch {
                  // ignore malformed metric_details
                }
              }

              if (!unitType || !unitNumber) {
                const flatMatch = entry.description?.match(/Daire\s+(\d+)\s+Satışı/i);
                const shopMatch = entry.description?.match(/Dükkan\s+(\d+)\s+Satışı/i);
                if (flatMatch) {
                  unitType = "flat";
                  unitNumber = Number(flatMatch[1]);
                } else if (shopMatch) {
                  unitType = "shop";
                  unitNumber = Number(shopMatch[1]);
                }
              }

              if (!unitType || !unitNumber || Number.isNaN(unitNumber)) return;

              const soldKey = `${entry.project_id}-${unitType}-${unitNumber}`;
              if (soldKeys.has(soldKey)) return;

              soldKeys.add(soldKey);
              soldByProject.set(entry.project_id, (soldByProject.get(entry.project_id) || 0) + 1);
            });

            const unitStats = active
              .map((project) => {
                const unitsFromList = project.details?.units?.length || 0;
                const totalUnitsForProject =
                  unitsFromList > 0
                    ? unitsFromList
                    : (project.details?.flats || 0) + (project.details?.shops || 0);

                const soldUnitsForProject = Math.min(
                  soldByProject.get(project.id) || 0,
                  totalUnitsForProject
                );
                const remainingUnitsForProject = Math.max(totalUnitsForProject - soldUnitsForProject, 0);

                return {
                  projectId: project.id,
                  projectName: project.name,
                  total: totalUnitsForProject,
                  sold: soldUnitsForProject,
                  remaining: remainingUnitsForProject,
                };
              })
              .filter((item) => item.total > 0);

            setProjectUnitStats(unitStats);
          } catch {
            setProjectUnitStats([]);
          }
          
        } else {
          
          setActiveProjects([]);
          setCompletedProjects([]);
          setProjects([]);
          setProjectUnitStats([]);
        }
      } catch (projectsError) {
        
        
        if (projectsError instanceof Error) {
          
        }
        toast({
          title: t('common.error'),
          description: t('projects.error_loading_projects') + ": " + 
            (projectsError instanceof Error ? projectsError.message : t('notifications.unknown_error')),
          variant: "destructive",
        });
        setHasError(true);
        setErrorMessage((projectsError instanceof Error ? projectsError.message : t('projects.error_loading_projects')));
        setActiveProjects([]);
        setCompletedProjects([]);
        setProjects([]);
        setProjectUnitStats([]);
      }
      
      // Fetch upcoming checks
      let checksForNotifications: any[] = [];
      try {
        
        const upcomingChecksData = await projectsAPI.getUpcomingCheckPayments();
        
        setUpcomingChecks(upcomingChecksData);
        checksForNotifications = upcomingChecksData;
      } catch (checksError) {
        
        
        if (checksError instanceof Error) {
          
        }
        toast({
          title: t('common.error'),
          description: t('projects.upcoming_payments') + " " + t('notifications.loading_error') + ": " + 
            (checksError instanceof Error ? checksError.message : t('notifications.unknown_error')),
          variant: "destructive",
        });
        setUpcomingChecks([]);
      }

      if (user?.id) {
        try {
          await notificationsAPI.syncCheckReminderNotifications(user.id, checksForNotifications);
          const inbox = await notificationsAPI.getNotifications(40);
          setNotifications(inbox.map(mapNotificationToItem));
        } catch {
          setNotifications([]);
        }
      }
      
      
    } catch (error) {
      
      
      if (error instanceof Error) {
        
      }
      toast({
        title: t('common.error'),
        description: t('notifications.loading_error') + ": " + 
          (error instanceof Error ? error.message : t('notifications.unknown_error')),
        variant: "destructive",
      });
      setHasError(true);
      setErrorMessage((error instanceof Error ? error.message : t('notifications.loading_error')));
    } finally {
      
      setIsLoading(false);
      isCurrentlyFetching.current = false;
      
    }
  }, [mapNotificationToItem, t, toast, user?.id]);

  // Simplified event listener registration
  useEffect(() => {
    const handleProjectDataRefresh = () => {
      
      
      // Clear any existing timeout
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      
      // Debounce the fetchData call
      debounceTimeout.current = setTimeout(() => {
        fetchData();
      }, 500);
    };
    
    document.addEventListener('project-data-refresh', handleProjectDataRefresh);
    
    return () => {
      document.removeEventListener('project-data-refresh', handleProjectDataRefresh);
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []); // Remove fetchData dependency to prevent recreation

  // Fetch data on mount and when user changes
  useEffect(() => {
    if (user?.id && has_access !== false) {
      
      fetchData();
    }
  }, [user?.id, has_access, fetchData]); // Include fetchData dependency

  // Expose a manual refresh function for pull-to-refresh or button use
  const refresh = useCallback(() => {
    
    fetchData();
  }, [fetchData]);

  // Move handleCreateNewProject outside of render function
  const handleCreateNewProject = useCallback(async () => {
    
    
    if (isCheckingLimits || isButtonDisabled) {
      
      return;
    }
    
    
    setIsCheckingLimits(true);
    setIsButtonDisabled(true);
    
    try {
      // Double-check the limits before navigation
      if (active_project_count >= active_project_limit) {
        
        toast({
          title: t('projects.project_limit_reached'),
          description: t('projects.project_limit_message', { limit: active_project_limit }),
          variant: "destructive"
        });
        navigate('/subscription');
        return;
      }
      
      
      // Check project limits
      const canCreate = await canCreateProject();
      
      
      if (!canCreate) {
        
        // Show a toast message about project limit
        toast({
          title: t('projects.project_limit_reached'),
          description: t('projects.project_limit_message', { limit: active_project_limit }),
          variant: "destructive"
        });
        
        // Navigate to subscription page
        navigate('/subscription');
        return;
      }
      
      
      // Navigate to create project page
      navigate('/new-project');
    } catch (error) {
      
      toast({
        title: t('common.error'),
        description: t('notifications.please_try_again'),
        variant: "destructive"
      });
    } finally {
      
      setIsCheckingLimits(false);
      setIsButtonDisabled(false);
    }
  }, [navigate, toast, t, active_project_count, active_project_limit, isButtonDisabled, isCheckingLimits]); // Include all necessary dependencies

  const canCreateProject = async () => {
    
    try {
      const result = await subscriptionsAPI.canCreateProject();
      
      return result.canCreate;
    } catch (error) {
      
      // Default to false on error
      return false;
    }
  };

  const handleCheckStatusClick = (check: any) => {
    
    setSelectedCheck(check);
    setCheckStatusDialogOpen(true);
  };

  const handleCheckStatusUpdate = async (status: 'alindi' | 'ertelendi' | 'alinamadi', newDueDate?: Date) => {
    
    
    if (!selectedCheck || !user?.id) {
      
      return;
    }

    try {
      
      // Update check status
      await entriesAPI.updateCheckStatus(
        selectedCheck.id,
        status,
        newDueDate,
        user.id
      );
      
      
      // Show success toast based on status
      let message = "";
      if (status === 'alindi') {
        message = selectedCheck.isGivenCheck 
          ? t('projects.check_paid')
          : t('projects.check_collected');
      } else if (status === 'ertelendi') {
        message = t('projects.check_postponed');
      } else {
        message = selectedCheck.isGivenCheck
          ? t('projects.check_not_paid')
          : t('projects.check_not_collected');
      }
      
      toast({
        title: t('notifications.operation_successful'),
        description: message,
      });
      
      
      // Refresh upcoming checks
      const upcomingChecksData = await projectsAPI.getUpcomingCheckPayments();
      setUpcomingChecks(upcomingChecksData);
      
    } catch (error) {
      
      toast({
        title: t('common.error'),
        description: t('errors.update_error'),
        variant: "destructive"
      });
    } finally {
      
      setCheckStatusDialogOpen(false);
      setSelectedCheck(null);
    }
  };

  // Add a subscription banner
  const renderSubscriptionBanner = () => {
    
    
    // Don't render anything if we're still loading the subscription data
    if (isSubscriptionLoading) {
      
      return null;
    }
    
    if (!has_access) {
      
      return (
        <div className="space-y-4 mb-4">
          <div className="bg-destructive/10 p-3 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <AlertCircle className="text-destructive mr-2 h-5 w-5" />
                <div>
                  <p className="text-sm font-medium">{t('subscription.subscription_expired')}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('subscription.subscription_expired_message')}
                  </p>
                </div>
              </div>
              {isMobilePlatform ? (
                <Button size="sm" variant="outline" onClick={() => navigate('/subscription-expired')}>
                  {t('subscription.subscribe')}
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => navigate('/subscription-expired')}>
                  {t('subscription.view_plans')}
                </Button>
              )}
            </div>
          </div>
          
          {/* Mobile store redirect message for PC users */}
          {!isMobilePlatform && (
            <MobileStoreRedirect variant="compact" />
          )}
        </div>
      );
    }
    
    
    return null;
  };

  const renderUpcomingChecks = () => {
    
    
    if (upcomingChecks.length === 0) {
      
      return null;
    }

    // Create today at midnight to compare dates accurately
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Set 7 days from now at end of day
    const sevenDaysFromNow = new Date(today);
    sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
    sevenDaysFromNow.setHours(23, 59, 59, 999); // End of the 7th day
    
    
    
    const filteredChecks = upcomingChecks.filter(check => {
      // Create date without time component by resetting to midnight
      const dueDate = new Date(check.dueDate);
      dueDate.setHours(0, 0, 0, 0);
      
      // Only include checks from today (>=) to 7 days from now (<=)
      return dueDate >= today && dueDate <= sevenDaysFromNow;
    });

    

    if (filteredChecks.length === 0) {
      // We had checks but none within our 7-day window
      if (upcomingChecks.some(check => {
        const dueDate = new Date(check.dueDate);
        dueDate.setHours(0, 0, 0, 0);
        return dueDate > sevenDaysFromNow;
      })) {
        // There are checks beyond the 7-day window
        return (
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-3 flex items-center">
              <CalendarClock className="h-5 w-5 mr-2 text-muted-foreground" />
              {t('projects.upcoming_payments')}
            </h2>
            <div className="bg-muted/50 p-4 rounded-lg text-sm text-center">
              {t('projects.no_upcoming_payments')}
              <Button 
                variant="outline" 
                className="mt-2 w-full justify-between" 
                onClick={() => navigate('/checks')}
              >
                {t('projects.view_all_checks')} 
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        );
      }
      
      return null;
    }

    return (
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-3 flex items-center">
          <CalendarClock className="h-5 w-5 mr-2 text-destructive" />
          {t('projects.upcoming_payments')}
        </h2>
        <div className="space-y-3">
          {filteredChecks.slice(0, 3).map((check) => {
            // Use dueInDays from the API to determine if the check is due today
            const isDueToday = check.dueInDays === 0;
            
            return (
              <Card key={check.id}
                className={`p-3 border ${check.dueInDays === 0 ? 'border-destructive bg-destructive/10' : 'border-amber-500/50 bg-amber-500/10'}`}
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-medium">{check.description}</p>
                    <p className="text-sm text-muted-foreground">{check.projectName}</p>
                    <div className="flex items-center text-xs mt-1">
                      <span className={`font-semibold ${check.dueInDays === 0 ? 'text-destructive' : 'text-amber-500'}`}>
                        {check.dueInDays === 0 ? t('projects.due_today') : check.dueInDays === 1 ? t('projects.due_tomorrow') : t('projects.due_in_days', { days: check.dueInDays })}
                      </span>
                      <span className="mx-1">•</span>
                      <span>{format(new Date(check.dueDate), "d MMMM yyyy", { locale: tr })}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="font-bold text-lg">₺{check.amount.toLocaleString()}</span>
                    <span className="text-xs text-muted-foreground mb-1">
                      {check.isGivenCheck ? t('projects.given_check') : t('projects.received_check')}
                    </span>
                    {check.dueInDays === 0 && (
                      <Button 
                        size="sm" 
                        className="mt-1" 
                        onClick={() => handleCheckStatusClick(check)}
                      >
                        {t('projects.update_status')}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
          {filteredChecks.length > 3 && (
            <Button 
              variant="outline" 
              className="w-full justify-between" 
              onClick={() => navigate('/checks')}
            >
              {t('projects.view_all_checks')} 
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  // Add new project button
  const renderNewProjectButton = () => {
    
    
    // If user doesn't have access at all, show subscription required button
    if (!has_access) {
      
      return (
        <Button
          className="h-[38px] rounded-[8px] border-[1.5px] border-[var(--brand-secondary,#3B608F)] bg-white px-[14px] text-[14px] font-semibold text-[var(--brand-900,#082D58)] shadow-[0_1px_3px_rgba(10,13,18,0.1),0_1px_2px_rgba(10,13,18,0.06)]"
          onClick={() => navigate('/subscription-expired')}
          variant="outline"
        >
          <span>{isMobilePlatform ? t('subscription.subscription_required') : t('subscription.subscription_required')}</span>
        </Button>
      );
    }
    
    // Check if projects are locked (subscription expired but still has access)
    if (projects_locked) {
      
      return (
        <Button
          className="h-[38px] rounded-[8px] border-[1.5px] border-[var(--brand-secondary,#3B608F)] bg-white px-[14px] text-[14px] font-semibold text-[var(--brand-900,#082D58)] shadow-[0_1px_3px_rgba(10,13,18,0.1),0_1px_2px_rgba(10,13,18,0.06)]"
          onClick={() => navigate('/subscription-expired')}
          variant="outline"
        >
          <LockIcon className="h-4 w-4" />
          <span>{t('subscription.projects_locked')}</span>
        </Button>
      );
    }
    
    // If user has access but reached project limit, show faded disabled button
    if (active_project_count >= active_project_limit) {
      
      return (
        <Button
          className="h-[38px] rounded-[8px] border-[1.5px] border-[var(--brand-secondary,#3B608F)] bg-white px-[14px] text-[14px] font-semibold text-[var(--gray-500,#667085)] opacity-60 shadow-[0_1px_3px_rgba(10,13,18,0.1),0_1px_2px_rgba(10,13,18,0.06)] cursor-not-allowed"
          disabled
          variant="outline"
        >
          <Plus className="h-4 w-4" />
          <span>{t('common.filter')} ({active_project_count}/{active_project_limit})</span>
        </Button>
      );
    }
    
    // If user can create projects, show the normal button
    
    return (
      <Button
        className="h-[38px] rounded-[8px] border-[1.5px] border-[var(--brand-secondary,#3B608F)] bg-white px-[14px] text-[14px] font-semibold text-[var(--brand-900,#082D58)] shadow-[0_1px_3px_rgba(10,13,18,0.1),0_1px_2px_rgba(10,13,18,0.06)] hover:bg-[var(--gray-50,#F8FAFB)]"
        onClick={handleCreateNewProject}
        disabled={isCheckingLimits || isButtonDisabled}
      >
        {isCheckingLimits ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Plus className="h-4 w-4" />
        )}
        <span>{t('projects.new_project')}</span>
      </Button>
    );
  };

  const totalUnits = activeProjects.reduce((sum, project) => {
    if (projectUnitStats.length > 0) {
      return sum;
    }
    const details = project.details;
    const unitsCount = details?.units?.length || 0;
    return sum + (unitsCount > 0 ? unitsCount : (details?.flats || 0) + (details?.shops || 0));
  }, 0);
  const summaryTotalUnits = projectUnitStats.length > 0
    ? projectUnitStats.reduce((sum, item) => sum + item.total, 0)
    : totalUnits;
  const summarySoldUnits = projectUnitStats.reduce((sum, item) => sum + item.sold, 0);
  const summaryRemainingUnits = projectUnitStats.reduce((sum, item) => sum + item.remaining, 0);

  const ongoingProjectsCount = activeProjects.filter((project) => project.status !== "delayed").length;
  const stoppedProjectsCount = activeProjects.filter((project) => project.status === "delayed").length;
  const selectedCurrencies = Array.from(
    new Set(
      [
        preferences?.base_currency || "TRY",
        preferences?.second_currency || "USD",
        preferences?.third_currency || "EUR",
      ].filter(Boolean)
    )
  );

  const normalizeCurrencyCode = (currency: string) => (currency === "KWD" ? "DINAR" : currency);

  const getCurrencySymbol = (currency: string) => {
    if (currency === "KWD") return "KWD";
    const normalized = normalizeCurrencyCode(currency);
    const match = SUPPORTED_CURRENCIES.find((item) => item.value === normalized);
    return match?.symbol || currency;
  };

  const getProjectCurrencyValue = (project: Project, currency: string, officialOnly: boolean) => {
    const primary = currency;
    const secondary = currency === "KWD" ? "DINAR" : currency === "DINAR" ? "KWD" : currency;
    const detailed = project.detailedCosts as any;

    // Shape A: { official: { TRY }, total: { TRY } }
    const shapeAValue =
      detailed?.[officialOnly ? "official" : "total"]?.[primary] ??
      detailed?.[officialOnly ? "official" : "total"]?.[secondary];
    if (typeof shapeAValue === "number") return shapeAValue;

    // Shape B: { TRY: { official, total } }
    const shapeBValue =
      detailed?.[primary]?.[officialOnly ? "official" : "total"] ??
      detailed?.[secondary]?.[officialOnly ? "official" : "total"];
    if (typeof shapeBValue === "number") return shapeBValue;

    // Fallback to legacy totals for core currencies
    if (!officialOnly) {
      if (primary === "TRY") return project.costs.try ?? 0;
      if (primary === "USD") return project.costs.usd ?? 0;
      if (primary === "EUR") return project.costs.eur ?? 0;
    }

    return 0;
  };

  useEffect(() => {
    if (!selectedCurrencies.includes(selectedCurrency)) {
      setSelectedCurrency(selectedCurrencies[0] || "TRY");
    }
  }, [selectedCurrencies, selectedCurrency]);

  const filteredActiveProjects = activeProjects.filter((project) => {
    const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase().trim());
    return matchesSearch;
  });

  return (
    <div className="page-container bg-[var(--brand-25,#F5F9FF)]">
      <main className="main-content !pt-0">
        <div className="pb-4">
          <div className="sticky top-0 z-20 bg-[var(--brand-900,#082D58)] px-5 pb-2 pt-[calc(var(--safe-area-top,0px)+0.5rem)] text-white">
            <div className="flex items-center justify-between gap-2">
              <div className="w-8" />
              <h1 className="flex-1 truncate text-center text-[18px] font-bold">{user?.company || user?.name || t('projects.my_projects')}</h1>
              <div className="flex items-center gap-3 text-brand-25">
                <NotificationsBell
                  items={notifications}
                  iconClassName="text-brand-25"
                />
              </div>
            </div>
          </div>

          <section className="rounded-b-[24px] bg-[var(--brand-900,#082D58)] px-5 pb-[22px] pt-3 text-white shadow-[0_2px_2px_rgba(16,24,40,0.06),0_4px_4px_rgba(16,24,40,0.1)]">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <p className="text-[13px] text-white/70">{t('projects.total_units', { defaultValue: 'Toplam ünite' })}</p>
                <p className="text-[34px] font-bold leading-none">{summaryTotalUnits}</p>
                <p className="mt-2 text-[12px] text-white/80">
                  Satılan: <span className="font-semibold text-white">{summarySoldUnits}</span>
                  {" • "}
                  Kalan: <span className="font-semibold text-white">{summaryRemainingUnits}</span>
                </p>
              </div>
              {renderNewProjectButton()}
            </div>

            <div className="mb-4 grid grid-cols-3 gap-2">
              <div className="rounded-[12px] border border-white/15 bg-white/10 p-2.5">
                <p className="mb-2 flex items-center gap-1.5 text-[12px] text-white/70">
                  <span className="inline-block h-2 w-2 shrink-0 rounded-[4px] bg-[#F87171] ring-1 ring-white/30" />
                  <span className="truncate">{t('projects.status.active')}</span>
                </p>
                <p className="text-[17px] font-semibold">{ongoingProjectsCount}</p>
              </div>
              <div className="rounded-[12px] border border-white/15 bg-white/10 p-2.5">
                <p className="mb-2 flex items-center gap-1.5 text-[12px] text-white/70">
                  <span className="inline-block h-2 w-2 shrink-0 rounded-[4px] bg-[#4ADE80] ring-1 ring-white/30" />
                  <span className="truncate">{t('projects.status.completed')}</span>
                </p>
                <p className="text-[17px] font-semibold">{completedProjects.length}</p>
              </div>
              <div className="rounded-[12px] border border-white/15 bg-white/10 p-2.5">
                <p className="mb-2 flex items-center gap-1.5 text-[12px] text-white/70">
                  <span className="inline-block h-2 w-2 shrink-0 rounded-[4px] bg-[#A4A7AE] ring-1 ring-white/30" />
                  <span className="truncate">{t('projects.status.delayed')}</span>
                </p>
                <p className="text-[17px] font-semibold">{stoppedProjectsCount}</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="inline-flex items-center gap-1 rounded-[999px] bg-white/10 p-1 backdrop-blur-sm">
                {selectedCurrencies.map((currency) => (
                  <button
                    type="button"
                    key={currency}
                    onClick={() => setSelectedCurrency(currency)}
                    className={selectedCurrency === currency
                      ? "h-7 min-w-[46px] rounded-[999px] border border-[#D2E6FF] bg-white px-3 text-[12px] font-semibold leading-5 text-[var(--brand-600,#1565C0)] shadow-[0_1px_2px_rgba(16,24,40,0.16)] transition-all"
                      : "h-7 min-w-[46px] rounded-[999px] border border-transparent bg-transparent px-3 text-[12px] font-medium leading-5 text-white/75 transition-all hover:text-white"}
                  >
                    {currency}
                  </button>
                ))}
              </div>
              <p className="flex items-center gap-1.5 text-[11px] text-[var(--brand-200,#B5DCFF)]">
                <Info className="h-[13px] w-[13px] shrink-0" />
                <span>Döviz kurlarını ayarlardan değiştirebilirsiniz.</span>
              </p>
            </div>

            {projectUnitStats.length > 0 && (
              <div className="mt-3 rounded-[12px] border border-white/15 bg-white/10 p-2.5">
                <p className="mb-1 text-[11px] font-semibold text-white/80">Projelere göre ünite durumu</p>
                <div className="space-y-1">
                  {projectUnitStats.map((item) => (
                    <div key={item.projectId} className="flex items-center justify-between gap-2 text-[11px] text-white/80">
                      <span className="truncate">{item.projectName}</span>
                      <span className="shrink-0">
                        {item.sold}/{item.total} satılan • {item.remaining} kalan
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          <div className="space-y-4 px-5 pt-6">
            {renderSubscriptionBanner()}

            <div>
              <h2 className="text-[18px] font-bold text-[var(--brand-900,#082D58)] leading-none">{t('projects.active_projects', { defaultValue: 'Aktif Projeler' })}</h2>
              <p className="mt-2 text-[11px] text-[var(--brand-800,#0C3F7A)]">
                {t('projects.search_help_text', { defaultValue: 'Projeye ait gelir, gider, malzeme ve anlaşılan fiyatları burada aratıp görebilirsiniz.' })}
              </p>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex flex-1 items-center gap-2 rounded-[8px] border border-[var(--gray-300,#D5D7DA)] bg-white px-3 py-2.5 shadow-sm">
                <Search className="h-4 w-4 text-[var(--gray-500,#717680)]" />
                <input
                  className="w-full bg-transparent text-[16px] text-[var(--gray-500,#717680)] outline-none placeholder:text-[var(--gray-500,#717680)]"
                  placeholder={t('common.search_placeholder', { defaultValue: 'Kayıt arayın' })}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button
                variant="outline"
                className="h-[44px] shrink-0 rounded-[8px] border-[1.5px] border-[var(--gray-100,#F5F5F5)] bg-white px-[14px] text-[14px] font-semibold text-[var(--brand-900,#082D58)] shadow-[0_1px_3px_rgba(10,13,18,0.1),0_1px_2px_rgba(10,13,18,0.06)]"
              >
                <SlidersHorizontal className="mr-2 h-4 w-4" />
                {t('common.filter')}
              </Button>
            </div>

            <div
              className={cn(
                "rounded-[14px] border px-4 py-3.5 transition-all duration-300 ease-out",
                showOfficialOnly
                  ? "border-[var(--brand-200,#B5DCFF)] bg-[linear-gradient(180deg,#F4F9FF_0%,#EAF3FF_100%)] shadow-[0_10px_24px_rgba(21,101,192,0.12)]"
                  : "border-[var(--gray-300,#D5D7DA)] bg-[var(--gray-50,#F2F6FA)] shadow-[0_1px_2px_rgba(16,24,40,0.06)]"
              )}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-[13px] font-semibold text-[var(--gray-900,#101828)]">{t('projects.official_records_only', { defaultValue: 'Sadece Resmi Kayıtları Göster' })}</p>
                  <p className="text-[12px] text-[var(--gray-500,#667085)]">
                    {showOfficialOnly
                      ? t('projects.only_official_records_shown', { defaultValue: 'Sadece resmi kayıtlar gösteriliyor' })
                      : t('projects.all_records_shown', { defaultValue: 'Tüm kayıtlar gösteriliyor' })}
                  </p>
                </div>
                <Switch
                  checked={showOfficialOnly}
                  onCheckedChange={setShowOfficialOnly}
                  aria-label={t('projects.official_records_only', { defaultValue: 'Sadece Resmi Kayıtları Göster' })}
                  className="shrink-0"
                />
              </div>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-7 w-7 animate-spin text-[var(--brand-600,#1565C0)]" />
              </div>
            ) : hasError ? (
              <div className="rounded-[12px] border border-[var(--error-600,#D92D20)]/20 bg-[var(--error-50,#FFF1F3)] p-4 text-center">
                <AlertCircle className="mx-auto mb-2 h-8 w-8 text-[var(--error-600,#D92D20)]" />
                <p className="mb-3 text-sm text-[var(--error-600,#D92D20)]">{errorMessage || t('notifications.loading_error')}</p>
                <Button variant="outline" onClick={() => window.location.reload()}>{t('common.tryAgain')}</Button>
              </div>
            ) : filteredActiveProjects.length === 0 && completedProjects.length === 0 ? (
              <div className="rounded-[12px] border border-[var(--gray-300,#D5D7DA)] bg-white p-6 text-center">
                <p className="mb-4 text-sm text-[var(--gray-600,#475467)]">{t('projects.no_projects_description')}</p>
                <div className="mx-auto w-fit">{renderNewProjectButton()}</div>
              </div>
            ) : (
              <div className="space-y-4">
                {renderUpcomingChecks()}
                {filteredActiveProjects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    showOfficialOnly={showOfficialOnly}
                    selectedCurrency={selectedCurrency}
                  />
                ))}
              </div>
            )}

            {completedProjects.length > 0 && (
              <Button
                variant="outline"
                className="w-full justify-between rounded-[8px] border-[var(--gray-300,#D5D7DA)] bg-white shadow-sm"
                onClick={() => navigate('/completed-projects')}
              >
                <span className="flex items-center">
                  <CheckSquare className="mr-2 h-4 w-4" />
                  {t('navigation.completedProjects')}
                </span>
                <span className="text-sm text-[var(--gray-500,#667085)]">{completedProjects.length}</span>
              </Button>
            )}
          </div>
        </div>
      </main>
      
      {/* Bottom Navigation */}
      <BottomNavigation />
      
      {/* Check Status Dialog */}
      {selectedCheck && (
        <CheckStatusDialog
          open={checkStatusDialogOpen}
          onOpenChange={setCheckStatusDialogOpen}
          isGivenCheck={selectedCheck.isGivenCheck}
          checkId={selectedCheck.id}
          checkAmount={selectedCheck.amount}
          checkDescription={selectedCheck.description}
          onStatusUpdate={handleCheckStatusUpdate}
        />
      )}
    </div>
  );
};

// Wrap HomePage with error boundary
const WrappedHomePage: React.FC = () => (
  <HomePageErrorBoundary>
    <HomePage />
  </HomePageErrorBoundary>
);

export default WrappedHomePage;


